import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Menu, MenuItem,
  Avatar, Tooltip, TextField, InputAdornment, TablePagination,
  FormControl, InputLabel, Select, Grid, Collapse, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Stack, Alert,
  CircularProgress
} from '@mui/material';
import {
  MoreVert, Search, CheckCircle, Schedule, AccountBalance,
  Visibility, GetApp, FilterList, ExpandMore, ExpandLess, Clear,
  Person, CalendarToday, Receipt, Phone, Work, Description,
  Replay, ErrorOutline
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import DialogCloseButton from '../common/DialogCloseButton';
import escrowService from '../../services/escrowService';
import { brand, text, bg, border } from '../../theme/colors';

/**
 * Improved Payment Table
 * Modern table with better UX, search, pagination, and actions
 */
const ImprovedPaymentTable = ({ payments }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundPayment, setRefundPayment] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundSuccess, setRefundSuccess] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [additionalFilters, setAdditionalFilters] = useState({
    minAmount: '',
    maxAmount: '',
    dateFrom: '',
    dateTo: '',
    paymentMethod: '',
    paymentType: '',
    reference: ''
  });

  // Status configuration
  const statusConfig = {
    escrow: { label: 'In Wallet', color: brand.primary, icon: <AccountBalance sx={{ fontSize: 16 }} /> },
    pending: { label: 'Pending', color: '#FAA61A', icon: <Schedule sx={{ fontSize: 16 }} /> },
    completed: { label: 'Completed', color: '#43B581', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
    available: { label: 'Available', color: '#43B581', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
    refunded: { label: 'Refunded', color: '#8E44AD', icon: <Replay sx={{ fontSize: 16 }} /> },
    failed: { label: 'Failed', color: '#E53935', icon: <ErrorOutline sx={{ fontSize: 16 }} /> }
  };

  // Enhanced filter logic
  const filteredPayments = payments.filter(payment => {
    const query = searchQuery.toLowerCase();

    // Enhanced search
    const contractorName = payment.contractor_name?.toLowerCase() || '';
    const contractorPhone = payment.contractor_phone?.toLowerCase() || '';
    const projectName = payment.project_name?.toLowerCase() || '';
    const description = payment.description?.toLowerCase() || '';
    const reference = payment.reference?.toLowerCase() || '';
    const amount = payment.amount?.toString() || '';

    const matchesSearch = contractorName.includes(query) ||
      contractorPhone.includes(query) ||
      projectName.includes(query) ||
      description.includes(query) ||
      reference.includes(query) ||
      amount.includes(query);

    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;

    const paymentAmount = parseFloat(payment.amount || 0);
    const matchesMinAmount = !additionalFilters.minAmount || paymentAmount >= parseFloat(additionalFilters.minAmount);
    const matchesMaxAmount = !additionalFilters.maxAmount || paymentAmount <= parseFloat(additionalFilters.maxAmount);

    const paymentDate = new Date(payment.created_at || payment.date);
    const matchesDateFrom = !additionalFilters.dateFrom || paymentDate >= new Date(additionalFilters.dateFrom);
    const matchesDateTo = !additionalFilters.dateTo || paymentDate <= new Date(additionalFilters.dateTo + 'T23:59:59');

    const matchesPaymentMethod = !additionalFilters.paymentMethod ||
      (payment.payment_method || '').toLowerCase().includes(additionalFilters.paymentMethod.toLowerCase());

    const matchesPaymentType = !additionalFilters.paymentType ||
      (payment.payment_type || payment.paymentType || 'milestone') === additionalFilters.paymentType;

    const matchesReference = !additionalFilters.reference ||
      reference.includes(additionalFilters.reference.toLowerCase());

    return matchesSearch && matchesStatus && matchesMinAmount && matchesMaxAmount &&
      matchesDateFrom && matchesDateTo && matchesPaymentMethod && matchesPaymentType && matchesReference;
  });

  const activeFilterCount = [
    additionalFilters.minAmount,
    additionalFilters.maxAmount,
    additionalFilters.dateFrom,
    additionalFilters.dateTo,
    additionalFilters.paymentMethod,
    additionalFilters.paymentType,
    additionalFilters.reference,
    statusFilter !== 'all' ? statusFilter : ''
  ].filter(Boolean).length;

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setAdditionalFilters({
      minAmount: '',
      maxAmount: '',
      dateFrom: '',
      dateTo: '',
      paymentMethod: '',
      paymentType: '',
      reference: ''
    });
  };

  // Paginate payments
  const paginatedPayments = filteredPayments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleMenuOpen = (event, payment) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleViewDetails = () => {
    setAnchorEl(null); // close menu but keep selectedPayment
    setDetailDialogOpen(true);
  };

  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false);
    setSelectedPayment(null);
  };

  const generateReceiptPDF = (payment) => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 15;
    let y = 0;

    // Header bar (Eqaya branding)
    pdf.setFillColor(37, 99, 235);
    pdf.rect(0, 0, pageWidth, 35, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PAYMENT RECEIPT', pageWidth / 2, 15, { align: 'center' });
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Eqaya Platform', pageWidth / 2, 23, { align: 'center' });
    pdf.setFontSize(8);
    pdf.text(`Receipt generated: ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, pageWidth / 2, 30, { align: 'center' });
    pdf.setTextColor(0, 0, 0);

    y = 45;

    // Reference & Date
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Reference:', margin, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(payment.reference || 'N/A', margin + 30, y);

    pdf.setFont('helvetica', 'bold');
    pdf.text('Date:', pageWidth / 2, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(formatDate(payment.created_at), pageWidth / 2 + 18, y);
    y += 12;

    // Divider
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 10;

    // Detail rows
    const addRow = (label, value) => {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text(label, margin, y);
      pdf.setFont('helvetica', 'normal');
      const text = String(value || 'N/A');
      const lines = pdf.splitTextToSize(text, pageWidth - margin * 2 - 45);
      pdf.text(lines, margin + 45, y);
      y += Math.max(8, lines.length * 6);
    };

    addRow('Contractor:', payment.contractor_name || 'Unknown');
    addRow('Phone:', payment.contractor_phone || 'N/A');
    addRow('Project:', payment.project_name || 'N/A');
    addRow('Description:', payment.description || 'N/A');
    addRow('Status:', (statusConfig[payment.status]?.label || payment.status || 'N/A').toUpperCase());
    if (payment.due_date) {
      addRow('Due Date:', formatDate(payment.due_date));
    }

    y += 5;
    pdf.line(margin, y, pageWidth - margin, y);
    y += 12;

    // Amount (prominent)
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Total Amount:', margin, y);
    pdf.setTextColor(37, 99, 235);
    const currencySymbol = payment.currency === 'USD' ? '$' : payment.currency || '$';
    pdf.text(
      `${currencySymbol}${parseFloat(payment.amount || 0).toLocaleString()}`,
      margin + 48, y
    );
    pdf.setTextColor(0, 0, 0);
    y += 15;

    // Milestones breakdown
    if (payment.milestones && payment.milestones.length > 0) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Milestones', margin, y);
      y += 8;

      payment.milestones.forEach((ms, i) => {
        // Check if we need a new page
        if (y > pdf.internal.pageSize.getHeight() - 40) {
          pdf.addPage();
          y = 20;
        }
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        const desc = ms.description || `Milestone ${i + 1}`;
        const amt = ms.amount ? ` - $${parseFloat(ms.amount).toLocaleString()}` : '';
        const status = ms.status ? ` [${ms.status}]` : '';
        pdf.text(`${i + 1}. ${desc}${amt}${status}`, margin + 5, y);
        y += 6;

        // Breakdown details
        if (ms.breakdown?.details) {
          pdf.setFontSize(8);
          pdf.setTextColor(100, 100, 100);
          const detailLines = pdf.splitTextToSize(ms.breakdown.details, pageWidth - margin * 2 - 20);
          pdf.text(detailLines, margin + 10, y);
          y += detailLines.length * 5;
          pdf.setTextColor(0, 0, 0);
        }

        // Breakdown sub-items
        if (ms.breakdown?.items?.length > 0) {
          pdf.setFontSize(8);
          ms.breakdown.items.forEach((si) => {
            if (y > pdf.internal.pageSize.getHeight() - 30) {
              pdf.addPage();
              y = 20;
            }
            const total = ((si.quantity || 0) * (si.unitPrice || 0)).toFixed(2);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`• ${si.name}  —  ${si.quantity} × $${si.unitPrice} = $${total}`, margin + 12, y);
            y += 5;
          });
          pdf.setTextColor(0, 0, 0);
          y += 2;
        }
      });
    }

    // Footer
    const footerY = pdf.internal.pageSize.getHeight() - 15;
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Generated by Eqaya Platform', margin, footerY);
    pdf.text(`Payment ID: ${payment.id}`, pageWidth - margin, footerY, { align: 'right' });

    // Save
    const filename = `eqaya-receipt-${payment.reference || payment.id || 'payment'}.pdf`;
    pdf.save(filename);
  };

  const handleDownloadReceipt = () => {
    if (!selectedPayment) {
      handleMenuClose();
      return;
    }
    const payment = selectedPayment;
    handleMenuClose();
    generateReceiptPDF(payment);
  };

  // An escrow payment qualifies for a refund request while funds are still
  // held in the wallet (escrow status) — not for property checks or payouts.
  const canRequestRefund = (payment) =>
    payment && payment.status === 'escrow' && !payment.payment_type;

  const handleOpenRefundDialog = () => {
    setRefundPayment(selectedPayment);
    setRefundReason('');
    setRefundError('');
    setRefundSuccess('');
    setRefundDialogOpen(true);
    handleMenuClose();
  };

  const handleSubmitRefund = async () => {
    if (refundReason.trim().length < 5) {
      setRefundError('Please describe why you are requesting a refund (at least 5 characters).');
      return;
    }
    setRefundSubmitting(true);
    setRefundError('');
    try {
      await escrowService.requestRefund(refundPayment.id, refundReason.trim());
      setRefundSuccess('Refund request submitted. An admin will review it and you will be notified.');
    } catch (err) {
      setRefundError(err.response?.data?.message || 'Failed to submit refund request');
    } finally {
      setRefundSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        {/* Header with Enhanced Filters */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payment History
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name, phone, project, reference..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, minWidth: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="escrow">In Wallet</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="available">Available</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              startIcon={showAdvancedFilters ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              sx={{ borderColor: brand.primary, color: brand.primary, borderWidth: 2, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
            >
              <FilterList sx={{ mr: 0.5 }} />
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </Button>

            {activeFilterCount > 0 && (
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={handleClearFilters}
                startIcon={<Clear />}
              >
                Clear
              </Button>
            )}
          </Box>

          {/* Advanced Filters */}
          <Collapse in={showAdvancedFilters}>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Min Amount"
                    type="number"
                    value={additionalFilters.minAmount}
                    onChange={(e) => setAdditionalFilters({...additionalFilters, minAmount: e.target.value})}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Max Amount"
                    type="number"
                    value={additionalFilters.maxAmount}
                    onChange={(e) => setAdditionalFilters({...additionalFilters, maxAmount: e.target.value})}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date From"
                    type="date"
                    value={additionalFilters.dateFrom}
                    onChange={(e) => setAdditionalFilters({...additionalFilters, dateFrom: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date To"
                    type="date"
                    value={additionalFilters.dateTo}
                    onChange={(e) => setAdditionalFilters({...additionalFilters, dateTo: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={additionalFilters.paymentMethod}
                      label="Payment Method"
                      onChange={(e) => setAdditionalFilters({...additionalFilters, paymentMethod: e.target.value})}
                    >
                      <MenuItem value="">All Methods</MenuItem>
                      <MenuItem value="ecocash">EcoCash</MenuItem>
                      <MenuItem value="onemoney">OneMoney</MenuItem>
                      <MenuItem value="worldremit">WorldRemit</MenuItem>
                      <MenuItem value="mukuru">Mukuru</MenuItem>
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      <MenuItem value="escrow">Wallet</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Type</InputLabel>
                    <Select
                      value={additionalFilters.paymentType}
                      label="Payment Type"
                      onChange={(e) => setAdditionalFilters({...additionalFilters, paymentType: e.target.value})}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="milestone">Milestone</MenuItem>
                      <MenuItem value="labor">Labor</MenuItem>
                      <MenuItem value="materials">Materials</MenuItem>
                      <MenuItem value="mixed">Mixed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reference Number"
                    value={additionalFilters.reference}
                    onChange={(e) => setAdditionalFilters({...additionalFilters, reference: e.target.value})}
                    placeholder="INV-2024-001"
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Contractor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" sx={{ color: text.secondary }}>
                      {searchQuery ? 'No payments found matching your search' : 'No payments yet'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPayments.map((payment) => (
                  <TableRow
                    key={payment.id}
                    sx={{
                      '&:hover': { bgcolor: bg.subtle, cursor: 'pointer' },
                      transition: 'background-color 0.2s ease'
                    }}
                    onClick={() => {
                      setSelectedPayment(payment);
                      setDetailDialogOpen(true);
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: brand.primary,
                            fontSize: 14,
                            fontWeight: 600
                          }}
                        >
                          {getInitials(payment.contractor_name)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {payment.contractor_name || 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {payment.project_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={payment.description || 'No description'}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {payment.description || 'No description'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: brand.primary }}>
                        ${parseFloat(payment.amount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: text.secondary }}>
                        {formatDate(payment.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={statusConfig[payment.status]?.icon}
                        label={statusConfig[payment.status]?.label || payment.status}
                        size="small"
                        sx={{
                          bgcolor: `${statusConfig[payment.status]?.color}20`,
                          color: statusConfig[payment.status]?.color,
                          fontWeight: 600,
                          border: `1px solid ${statusConfig[payment.status]?.color}40`
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, payment);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredPayments.length > 0 && (
          <TablePagination
            component="div"
            count={filteredPayments.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewDetails}>
            <Visibility sx={{ mr: 1, fontSize: 20 }} />
            View Details
          </MenuItem>
          <MenuItem onClick={handleDownloadReceipt}>
            <GetApp sx={{ mr: 1, fontSize: 20 }} />
            Download Receipt
          </MenuItem>
          {canRequestRefund(selectedPayment) && (
            <MenuItem onClick={handleOpenRefundDialog}>
              <Replay sx={{ mr: 1, fontSize: 20 }} />
              Request Refund
            </MenuItem>
          )}
        </Menu>

        {/* Refund Request Dialog */}
        <Dialog
          open={refundDialogOpen}
          onClose={() => !refundSubmitting && setRefundDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogCloseButton onClick={() => !refundSubmitting && setRefundDialogOpen(false)} />
          <DialogTitle>Request Refund</DialogTitle>
          <DialogContent>
            {refundSuccess ? (
              <Alert severity="success" sx={{ mt: 1 }}>{refundSuccess}</Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Request a refund of the unspent funds held in your project wallet
                  {refundPayment ? ` for "${refundPayment.project_name}"` : ''}.
                  Funds already released for completed milestones cannot be refunded.
                  An admin reviews every refund request before money moves; refunds are
                  returned to your original payment method and typically arrive within
                  5–10 business days.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Reason for refund"
                  placeholder="e.g., Project cancelled, contractor unavailable, change of plans..."
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  disabled={refundSubmitting}
                />
                {refundError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{refundError}</Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRefundDialogOpen(false)} disabled={refundSubmitting}>
              {refundSuccess ? 'Close' : 'Cancel'}
            </Button>
            {!refundSuccess && (
              <Button
                onClick={handleSubmitRefund}
                variant="contained"
                color="warning"
                disabled={refundSubmitting || refundReason.trim().length < 5}
                startIcon={refundSubmitting ? <CircularProgress size={16} /> : <Replay />}
              >
                {refundSubmitting ? 'Submitting...' : 'Submit Refund Request'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Payment Detail Dialog */}
        <Dialog
          open={detailDialogOpen && !!selectedPayment}
          onClose={handleDetailDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogCloseButton onClick={handleDetailDialogClose} />
          {selectedPayment && (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt sx={{ color: brand.primary }} />
                Payment Details
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2.5}>
                  {/* Contractor Info */}
                  <Box>
                    <Typography variant="overline" color="text.secondary">Contractor</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                      <Avatar sx={{ bgcolor: brand.primary, width: 40, height: 40 }}>
                        {getInitials(selectedPayment.contractor_name)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {selectedPayment.contractor_name || 'Unknown'}
                        </Typography>
                        {selectedPayment.contractor_phone && selectedPayment.contractor_phone !== 'N/A' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {selectedPayment.contractor_phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Divider />

                  {/* Project Info */}
                  <Box>
                    <Typography variant="overline" color="text.secondary">Project</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <Work sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body1" fontWeight={500}>
                        {selectedPayment.project_name || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Amount Highlight */}
                  <Box
                    sx={{
                      p: 2.5,
                      bgcolor: '#eff6ff',
                      borderRadius: 2,
                      border: '1px solid #bfdbfe',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                      Total Amount
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: brand.primary, mt: 0.5 }}>
                      ${parseFloat(selectedPayment.amount || 0).toLocaleString()}
                    </Typography>
                    {selectedPayment.currency && selectedPayment.currency !== 'USD' && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedPayment.currency}
                      </Typography>
                    )}
                  </Box>

                  {/* Details Grid */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="text.secondary">Status</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          icon={statusConfig[selectedPayment.status]?.icon}
                          label={statusConfig[selectedPayment.status]?.label || selectedPayment.status}
                          size="small"
                          sx={{
                            bgcolor: `${statusConfig[selectedPayment.status]?.color}20`,
                            color: statusConfig[selectedPayment.status]?.color,
                            fontWeight: 600,
                            border: `1px solid ${statusConfig[selectedPayment.status]?.color}40`
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="text.secondary">Date</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{formatDate(selectedPayment.created_at)}</Typography>
                      </Box>
                    </Grid>
                    {selectedPayment.reference && (
                      <Grid item xs={6}>
                        <Typography variant="overline" color="text.secondary">Reference</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                          {selectedPayment.reference}
                        </Typography>
                      </Grid>
                    )}
                    {selectedPayment.due_date && (
                      <Grid item xs={6}>
                        <Typography variant="overline" color="text.secondary">Due Date</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{formatDate(selectedPayment.due_date)}</Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Description */}
                  {selectedPayment.description && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="overline" color="text.secondary">Description</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
                          <Description sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
                          <Typography variant="body2">{selectedPayment.description}</Typography>
                        </Box>
                      </Box>
                    </>
                  )}

                  {/* Milestones Breakdown */}
                  {selectedPayment.milestones && selectedPayment.milestones.length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="overline" color="text.secondary">Milestones</Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          {selectedPayment.milestones.map((ms, i) => (
                            <Box
                              key={ms.id || i}
                              sx={{
                                p: 1.5,
                                bgcolor: bg.subtle,
                                borderRadius: 1,
                                border: '1px solid #e9ecef'
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {ms.description || `Milestone ${i + 1}`}
                                  </Typography>
                                  {ms.status && (
                                    <Chip
                                      label={ms.status}
                                      size="small"
                                      sx={{ mt: 0.5, fontSize: 11, height: 22 }}
                                    />
                                  )}
                                </Box>
                                {ms.amount && (
                                  <Typography variant="body2" fontWeight={600} sx={{ color: brand.primary }}>
                                    ${parseFloat(ms.amount).toLocaleString()}
                                  </Typography>
                                )}
                              </Box>
                              {/* Breakdown details */}
                              {ms.breakdown?.details && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {ms.breakdown.details}
                                </Typography>
                              )}
                              {/* Breakdown sub-items */}
                              {ms.breakdown?.items?.length > 0 && (
                                <Box sx={{ mt: 0.75, pl: 1, borderLeft: `2px solid ${border.default}` }}>
                                  {ms.breakdown.items.map((si, j) => (
                                    <Box key={j} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        {si.name} — {si.quantity} x ${parseFloat(si.unitPrice || 0).toFixed(2)}
                                      </Typography>
                                      <Typography variant="caption" fontWeight={500}>
                                        ${((si.quantity || 0) * (si.unitPrice || 0)).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleDetailDialogClose}>Close</Button>
                <Button
                  variant="contained"
                  startIcon={<GetApp />}
                  onClick={() => generateReceiptPDF(selectedPayment)}
                  sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
                >
                  Download Receipt
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ImprovedPaymentTable;
